/**
 * Manual KYC for Hyperverge questions (Switch to Manual after HV failure)
 * and legacy document-question typed fields.
 */

/** Hyperverge / DigiLocker question IDs that support Switch to Manual. */
export const HYPERVERGE_MANUAL_QUESTIONS = {
  QBM1000002: { type: "PAN", label: "PAN Number" },
  QBM1000015: { type: "GSTIN", label: "GSTIN Number" },
  QBM1000014: { type: "BANK", label: "Bank Account" },
  QBM1000043: { type: "DIRECTOR_AADHAAR", label: "Director Aadhaar" },
};

/** Legacy document KYC questions (typed + upload). Kept for existing answers. */
export const MANUAL_KYC_DOCUMENT_QUESTIONS = {
  QBM1000033: { type: "PAN", label: "PAN Number" },
  QBM1000040: { type: "PAN", label: "PAN Number" },
  QBM1000024: { type: "GSTIN", label: "GSTIN Number" },
  QBM1000025: { type: "BANK", label: "Bank Account" },
  QBM1000022: { type: "DIRECTOR_AADHAAR", label: "Aadhaar Number" },
  QBM1000023: { type: "DIRECTOR_PAN", label: "PAN Number" },
  QBM1000044: { type: "COMPANY_ADDRESS", label: "Company Address" },
  QBM1000102: { type: "COMPANY_ADDRESS", label: "Company Address" },
};

/** @deprecated Manual KYC thread is retired — Switch to Manual is used instead. */
export const MANUAL_KYC_PREDEFINED_THREAD_NAME = "Manual KYC";

export const MANUAL_KYC_BANK_FIELD_NAMES = [
  "ifsc",
  "accNum",
  "bankName",
  "branchName",
  "accHolderName",
];

export const MANUAL_KYC_DIRECTOR_AADHAAR_FIELD_NAMES = [
  "directorName",
  "aadhaar",
  "pan",
];

export const MANUAL_KYC_COMPANY_ADDRESS_FIELD_NAMES = [
  "addressLine1",
  "addressLine2",
  "city",
  "pincode",
  "state",
];

/** GSTIN Switch-to-Manual also captures communication address. */
export const MANUAL_KYC_GSTIN_FIELD_NAMES = [
  "gstin",
  ...MANUAL_KYC_COMPANY_ADDRESS_FIELD_NAMES,
];

export const isHypervergeManualEligible = (questionId) =>
  Boolean(HYPERVERGE_MANUAL_QUESTIONS[questionId]);

export const getHypervergeManualType = (questionId) =>
  HYPERVERGE_MANUAL_QUESTIONS[questionId]?.type || null;

export const isManualKycDocumentQuestion = (questionId) =>
  Boolean(MANUAL_KYC_DOCUMENT_QUESTIONS[questionId]);

/** @deprecated Prefer isHypervergeManualEligible + isManualMode */
export const isManualKycThread = (threadName) =>
  String(threadName || "")
    .trim()
    .toLowerCase() === MANUAL_KYC_PREDEFINED_THREAD_NAME.toLowerCase();

/**
 * Show typed manual fields when:
 * - user switched to manual on a Hyperverge question, OR
 * - legacy document question on Manual KYC thread (back-compat)
 */
export const shouldShowManualKycFields = (
  questionId,
  threadName,
  isManualMode = false,
) => {
  if (isManualMode && isHypervergeManualEligible(questionId)) return true;
  return (
    isManualKycDocumentQuestion(questionId) && isManualKycThread(threadName)
  );
};

export const getManualKycDocumentType = (questionId) =>
  HYPERVERGE_MANUAL_QUESTIONS[questionId]?.type ||
  MANUAL_KYC_DOCUMENT_QUESTIONS[questionId]?.type ||
  null;

export const isManualKycFileUploadRequired = (
  questionId,
  threadName,
  isManualMode = false,
) => {
  if (!shouldShowManualKycFields(questionId, threadName, isManualMode)) {
    return false;
  }
  const type = getManualKycDocumentType(questionId);
  return Boolean(type) && type !== "COMPANY_ADDRESS";
};

export const buildManualKycAnswerText = (type, values = {}) => {
  const withMode = (obj) =>
    JSON.stringify({ ...obj, entryMode: "MANUAL" });

  if (type === "PAN" || type === "DIRECTOR_PAN") {
    return withMode({ pan: String(values.pan || "").toUpperCase().trim() });
  }
  if (type === "GSTIN") {
    return withMode({
      gstin: String(values.gstin || "").toUpperCase().trim(),
      addressLine1: String(values.addressLine1 || "").trim(),
      addressLine2: String(values.addressLine2 || "").trim(),
      city: String(values.city || "").trim(),
      state: String(values.state || "").trim(),
      pinCode: String(values.pincode || "").replace(/\D/g, ""),
    });
  }
  if (type === "BANK") {
    return withMode({
      ifsc: String(values.ifsc || "").toUpperCase().trim(),
      accNum: String(values.accNum || "").replace(/\D/g, ""),
      bankName: String(values.bankName || "").trim(),
      branchName: String(values.branchName || "").trim(),
      accHolderName: String(values.accHolderName || "").trim(),
    });
  }
  if (type === "DIRECTOR_AADHAAR") {
    return withMode({
      directorName: String(values.directorName || "").trim(),
      aadhaar: String(values.aadhaar || "").replace(/\D/g, ""),
      pan: String(values.pan || "").toUpperCase().trim(),
      ...(values.panFileUrl
        ? { panFileUrl: String(values.panFileUrl).trim() }
        : {}),
    });
  }
  if (type === "COMPANY_ADDRESS") {
    return withMode({
      addressLine1: String(values.addressLine1 || "").trim(),
      addressLine2: String(values.addressLine2 || "").trim(),
      city: String(values.city || "").trim(),
      state: String(values.state || "").trim(),
      pinCode: String(values.pincode || "").replace(/\D/g, ""),
    });
  }
  return null;
};

export const parseManualKycAnswerText = (answerText) => {
  if (!answerText || typeof answerText !== "string") return null;
  const trimmed = answerText.trim();
  if (!trimmed.startsWith("{")) return { raw: trimmed };
  try {
    return JSON.parse(trimmed);
  } catch {
    return { raw: trimmed };
  }
};

export const isManualEntryAnswer = (answerText) => {
  const parsed = parseManualKycAnswerText(answerText);
  return Boolean(
    parsed &&
      !parsed.raw &&
      (parsed.entryMode === "MANUAL" ||
        parsed.pan ||
        parsed.gstin ||
        parsed.accNum ||
        parsed.aadhaar),
  );
};
